import useMobile from 'hooks/useMobile';
import { Container, Heading, Image, Link, Title } from './common';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { routePaths } from 'appConfig';
import cn from 'classnames';
import { Carousel } from './common/v2';

const offers: {
  id: string;
  title: string;
  description: string;
  img: string;
  url: string;
}[] = [
  {
    id: 'catering',
    title: 'Catering Services',
    description:
      'Hot-served bento sets, buffets, and light bites for events with more than 20 guests.',
    img: '/assets/images/offers/catering.png',
    url: routePaths.CATERING,
  },
  {
    id: 'yb-express',
    title: 'YB Express',
    description:
      'Need it fast? Our express menu gets meals to your door within 2 hours, islandwide.',
    img: '/assets/images/offers/yb-express.png',
    url: routePaths.YB_EXPRESS,
  },
  {
    id: 'gift-cards',
    title: 'Gift Cards',
    description:
      'Because eating well is always a good idea, and even better when it’s gifted.',
    img: '/assets/images/offers/gift-cards.png',
    url: routePaths.GIFT_CARDS,
  },
];

const OfferCard = ({
  offer,
  hovered,
  setHovered,
  aspectRatio = 'tw-aspect-square',
}: {
  offer: typeof offers[0];
  hovered: string | null;
  setHovered: Dispatch<SetStateAction<string | null>>;
  aspectRatio?: string;
}) => {
  return (
    <Link key={offer.id} to={offer.url}>
      <div
        onMouseEnter={() => setHovered(offer.id)}
        onMouseLeave={() => setHovered(null)}
        className={cn(
          'tw-relative tw-overflow-hidden tw-w-full tw-transition-transform tw-duration-500 tw-ease-out tw-rounded-md',
          aspectRatio,
          hovered !== null &&
            hovered === offer.id &&
            'tw-scale-[1.02] tw-shadow-[0_10px_30px_rgba(0,0,0,0.18),0_4px_12px_rgba(0,0,0,0.12)]',
        )}
      >
        <Image
          src={offer.img}
          layout="fill"
          objectFit="cover"
          className="tw-absolute tw-inset-0"
          priority
          alt={offer.id}
        />

        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/60 tw-via-black/5 tw-to-transparent" />
        <div
          className="tw-absolute tw-inset-0 tw-flex tw-items-start tw-flex-col tw-justify-end tw-py-8 tw-px-8 tw-transition-opacity tw-duration-300 tw-select-none"
          tabIndex={-1}
        >
          <h3 className="tw-font-bold tw-bg-clip-text tw-text-transparent tw-bg-gradient-to-b tw-from-white tw-to-white-100 tw-text-4xl tw-drop-shadow-md tw-mb-1">
            {offer.title}
          </h3>
          <p className="tw-mb-0 tw-text-white tw-leading-tight tw-drop-shadow-sm">
            {offer.description}
          </p>
        </div>
      </div>
    </Link>
  );
};

const SpecialOffersGrid = ({
  specialOfferings,
}: {
  specialOfferings: typeof offers;
}) => {
  const [hovered, setHovered] = useState<string | null>(null);

  const gridColsClassName = useMemo(() => {
    const length = specialOfferings.length;
    if (length > 2) return 'lg:tw-grid-cols-3';
    if (length > 1) return 'lg:tw-grid-cols-2';
    return 'lg:tw-grid-cols-1';
  }, [specialOfferings.length]);

  const aspectRatioClassName = useMemo(() => {
    if (specialOfferings.length > 2) return 'tw-aspect-square';
    if (specialOfferings.length > 1) return 'tw-aspect-video';
    return 'tw-aspect-square md:tw-aspect-auto md:tw-h-[360px]';
  }, [specialOfferings.length]);

  return (
    <div
      className={cn('tw-grid tw-gap-8 tw-w-full', gridColsClassName)}
    >
      {specialOfferings.map((offer) => (
        <OfferCard
          key={offer.id}
          hovered={hovered}
          setHovered={setHovered}
          offer={offer}
          aspectRatio={aspectRatioClassName}
        />
      ))}
    </div>
  );
};

interface SpecialOfferingsProps {
  title?: string;
  titleClassName?: string;
  isHorizontalScrollOnMobile?: boolean;
  cateringEnabled?: boolean;
  ybExpressEnabled?: boolean;
}

const SpecialOfferings = ({
  title,
  titleClassName,
  isHorizontalScrollOnMobile = true,
  cateringEnabled,
  ybExpressEnabled,
}: SpecialOfferingsProps) => {
  const isMobile = useMobile();

  const specialOfferings = useMemo(
    () =>
      offers
        .filter((offer) => {
          if (!cateringEnabled && offer.id === 'catering')
            return false;
          if (!ybExpressEnabled && offer.id === 'yb-express')
            return false;
          return true;
        })
        .map((offer) => offer),
    [cateringEnabled, ybExpressEnabled],
  );

  return (
    <Container className="tw-mt-6 xl:tw-mt-12 pt-0 tw-pb-16 xl:tw-pb-8">
      <div className="tw-flex tw-flex-col">
        {title && (
          <Heading>
            <Title
              className={cn(
                'tw-text-4xl tw-font-bold tw-mb-4 tw-text-center',
                titleClassName,
              )}
            >
              {title}
            </Title>
          </Heading>
        )}
        {isMobile && isHorizontalScrollOnMobile ? (
          <div>
            <Carousel
              slidesToShow={{ base: 1 }}
              options={{ align: 'start' }}
              wheelGesture
            >
              {specialOfferings.map((offer) => (
                <OfferCard
                  key={offer.id}
                  hovered={null}
                  setHovered={() => {}}
                  offer={offer}
                />
              ))}
            </Carousel>
          </div>
        ) : (
          <SpecialOffersGrid specialOfferings={specialOfferings} />
        )}
      </div>
    </Container>
  );
};

export default SpecialOfferings;
